import React, { useState, useEffect } from "react";
import {
  useClothesStore,
  ClothesItem,
} from "../../store/clothesStore";
import { useToast } from "../../components/ToastContainer";
import { useApiCall } from "../../hooks/useApiCall";
import Loader from "../../components/Loader";
import EditClothesModal from "../../components/EditClothesModal";
import ClothesTable from "../../components/ClothesTable";

interface ClothesInventoryProps {
  searchQuery: string;
  onAddClothes: () => void;
}

const ClothesInventory: React.FC<ClothesInventoryProps> = ({
  searchQuery,
  onAddClothes,
}) => {
  const {
    groupedItems: clothesGroupedItems,
    isLoading: clothesIsLoading,
    searchClothes,
    searchResults: clothesSearchResults,
    isSearching: clothesIsSearching,
    clearSearchResults: clothesClearSearchResults,
    deleteClothes,
    currentPage: storePage,
    totalItems,
    itemsPerPage,
    setPage,
    setItemsPerPage,
    searchCurrentPage,
    searchTotalItems,
    searchItemsPerPage,
    setSearchPage,
    setSearchItemsPerPage,
  } = useClothesStore();

  const { showSuccess, showError } = useToast();
  const { executeApiCall } = useApiCall();

  const [editClothesModalOpen, setEditClothesModalOpen] = useState(false);
  const [editingClothes, setEditingClothes] = useState<ClothesItem | null>(null);

  // Поиск
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery.trim()) {
        searchClothes(searchQuery);
      } else {
        clothesClearSearchResults();
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, searchClothes, clothesClearSearchResults]);

  // Определяем текущие данные
  const isSearchMode = searchQuery.trim() !== "";
  const currentItems = isSearchMode ? clothesSearchResults : clothesGroupedItems;
  const isPaginationLoading = isSearchMode ? clothesIsSearching : clothesIsLoading;

  // Пагинация
  const currentPageToUse = isSearchMode ? searchCurrentPage + 1 : storePage + 1;
  const totalItemsToUse = isSearchMode ? searchTotalItems : totalItems;
  const itemsPerPageToUse = isSearchMode ? searchItemsPerPage : itemsPerPage;

  const handlePageChange = (newPage: number) => {
    if (isSearchMode) {
      setSearchPage(newPage - 1);
    } else {
      setPage(newPage - 1);
    }
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    if (isSearchMode) {
      setSearchItemsPerPage(newItemsPerPage);
    } else {
      setItemsPerPage(newItemsPerPage);
    }
  };

  const formatPrice = (price: string): string => {
    if (!price) return "0 ₽";
    
    if (price.includes(" - ")) {
      const [min, max] = price.split(" - ");
      const cleanMin = min.replace(/[^\d]/g, "");
      const cleanMax = max.replace(/[^\d]/g, "");
      return `${parseInt(cleanMin).toLocaleString()} - ${parseInt(cleanMax).toLocaleString()} ₽`;
    }
    
    const cleanPrice = price.replace(/[^\d]/g, "");
    if (!cleanPrice) return "0 ₽";
    return `${parseInt(cleanPrice).toLocaleString()} ₽`;
  };

  const handleEditClothes = async (clothesItem: ClothesItem) => {
    try {
      setEditingClothes(clothesItem);
      setEditClothesModalOpen(true);
    } catch (error) {
      console.error("Ошибка при открытии модального окна редактирования одежды:", error);
    }
  };

  const handleDelete = (id: number) => {
    if (window.confirm("Вы уверены, что хотите удалить этот товар?")) {
      executeApiCall(() => deleteClothes(id), {
        successMessage: "Одежда успешно удалена из инвентаря",
      });
    }
  };

  const handleCopyLink = async (id: number) => {
    const link = `${window.location.origin}/clothes/${id}`;
    try {
      await navigator.clipboard.writeText(link);
      showSuccess("Ссылка скопирована в буфер обмена");
    } catch (error) {
      showError("Не удалось скопировать ссылку");
    }
  };

  if (isPaginationLoading && currentItems.length === 0) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "300px",
        }}
      >
        <Loader size="medium" text="Загружаем страницу..." />
      </div>
    );
  }

  return (
    <>
      <ClothesTable
        data={currentItems}
        isLoading={isPaginationLoading}
        onEdit={handleEditClothes}
        onDelete={handleDelete}
        onCopyLink={handleCopyLink}
        formatPrice={formatPrice}
        currentPage={currentPageToUse}
        totalItems={totalItemsToUse}
        itemsPerPage={itemsPerPageToUse}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
      />

      {editingClothes && (
        <EditClothesModal
          isOpen={editClothesModalOpen}
          onClose={() => {
            setEditClothesModalOpen(false);
            setEditingClothes(null);
          }}
          clothesItem={editingClothes}
        />
      )}
    </>
  );
};

export default ClothesInventory;
